import { computed, DestroyRef, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, defer, EMPTY, finalize, Observable, tap, throwError } from 'rxjs';
import { TestPlanPackage, TestPlanPackageRequest } from '../../models/test-plan-package.model';
import { PaketDialogErgebnis, PaketTestfall } from '../paket-testfall-dialog/paket-testfall-dialog.types';
import { ProjectEnvironment } from '../../models/project-environment.model';
import { ProjectTestStage } from '../../models/project.model';
import { Release } from '../../models/release.model';
import { CreatedTestRun } from '../../models/test-run.model';
import { TestPlanPackageService } from '../../services/test-plan-package.service';
import { ProjectEnvironmentService } from '../../services/project-environment.service';
import { ProjectTestStageService } from '../../services/project-test-stage.service';
import { isStaleVersion } from '../../shared/http/server-message';

export type PaketLadeStatus = 'laedt' | 'fehler' | 'bereit';
export type PaketSpeicherFehler = 'veraltet' | 'fehler';

export interface Nachschlag<T> {
  status: PaketLadeStatus;
  eintraege: T[];
}

export interface PaketDialogZustand {
  /** `null` legt ein neues Paket an. */
  paketKey: string | null;
}

/** Bearbeitbarer Stand eines Pakets; `key` bleibt stabil, auch solange das Paket noch keine `id` hat. */
export interface PaketEntwurf {
  key: string;
  id: string | null;
  name: string;
  projectTestStageId: string | null;
  environments: string[];
  testCaseIds: string[];
}

export type PaketAenderung = Partial<
  Pick<PaketEntwurf, 'name' | 'environments' | 'testCaseIds' | 'projectTestStageId'>
>;

/** Flüchtiger Stand eines in dieser Sitzung gestarteten Probelaufs (P16). */
export type PaketLauf = { status: 'laeuft' } | { status: 'fertig'; laeufe: CreatedTestRun[] };

export interface StufenOption {
  id: string;
  name: string;
}

export interface PaketFilter {
  suche: string;
  /** ID der Teststufe, leer = alle. */
  stufe: string;
  /** Name der Umgebung, leer = alle. */
  umgebung: string;
  nurUnvollstaendig: boolean;
}

const ERSTE_ANZAHL = 25;
const MEHR_LADEN = 50;
const OHNE_FILTER: PaketFilter = { suche: '', stufe: '', umgebung: '', nurUnvollstaendig: false };

@Injectable()
export class TestpaketeStore {
  private readonly paketService = inject(TestPlanPackageService);
  private readonly umgebungService = inject(ProjectEnvironmentService);
  private readonly stufenService = inject(ProjectTestStageService);
  private readonly destroyRef = inject(DestroyRef);

  private planId: string | null = null;
  private neueSchluessel = 0;

  private readonly _projektId = signal('');
  private readonly _gespeichert = signal<TestPlanPackage[]>([]);
  private readonly _entwurf = signal<PaketEntwurf[]>([]);
  private readonly _ladeStatus = signal<PaketLadeStatus>('laedt');
  private readonly _speicherFehler = signal<PaketSpeicherFehler | null>(null);
  private readonly _dialog = signal<PaketDialogZustand | null>(null);
  private readonly _umgebungen = signal<Nachschlag<ProjectEnvironment>>({ status: 'laedt', eintraege: [] });
  private readonly _projektStufen = signal<Nachschlag<ProjectTestStage>>({ status: 'laedt', eintraege: [] });
  private readonly _filter = signal<PaketFilter>(OHNE_FILTER);
  private readonly _anzahl = signal(ERSTE_ANZAHL);
  private readonly _markiert = signal<string[]>([]);
  private readonly _offenKey = signal<string | null>(null);
  private readonly _laeufe = signal<Record<string, PaketLauf>>({});
  private readonly _umgebungAnlage = signal(false);

  readonly projektId = this._projektId.asReadonly();
  readonly gespeichert = this._gespeichert.asReadonly();
  readonly entwurf = this._entwurf.asReadonly();
  readonly ladeStatus = this._ladeStatus.asReadonly();
  readonly speicherFehler = this._speicherFehler.asReadonly();
  readonly dialog = this._dialog.asReadonly();
  readonly umgebungen = this._umgebungen.asReadonly();
  readonly projektStufen = this._projektStufen.asReadonly();
  readonly filter = this._filter.asReadonly();
  readonly anzahl = this._anzahl.asReadonly();
  readonly markiert = this._markiert.asReadonly();
  readonly offenKey = this._offenKey.asReadonly();
  readonly laeufe = this._laeufe.asReadonly();
  readonly umgebungAnlage = this._umgebungAnlage.asReadonly();
  readonly geaendert = computed(
    () =>
      JSON.stringify(this._entwurf().map(vergleichswert)) !== JSON.stringify(this._gespeichert().map(vergleichswert)),
  );

  init(projectId: string, planId: string | null): void {
    if (projectId !== this._projektId()) {
      this._umgebungen.set({ status: 'laedt', eintraege: [] });
      this._projektStufen.set({ status: 'laedt', eintraege: [] });
    }
    this._projektId.set(projectId);
    this.planId = planId;
    this._gespeichert.set([]);
    this._entwurf.set([]);
    this._speicherFehler.set(null);
    this._dialog.set(null);
    this._ladeStatus.set('laedt');
    this._filter.set(OHNE_FILTER);
    this._anzahl.set(ERSTE_ANZAHL);
    this._markiert.set([]);
    this._offenKey.set(null);
    this._laeufe.set({});
  }

  laden(): void {
    const planId = this.planId;
    if (!planId) {
      this.uebernehmeGespeichert([]);
      this._ladeStatus.set('bereit');
      return;
    }
    this._ladeStatus.set('laedt');
    this.paketService
      .list(this._projektId(), planId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (pakete) => {
          this.uebernehmeGespeichert(pakete);
          this._ladeStatus.set('bereit');
        },
        error: () => this._ladeStatus.set('fehler'),
      });
  }

  /** Lädt Umgebungen und Projekt-Teststufen; was schon geladen ist, bleibt. */
  ladenNachschlag(): void {
    this.ladeListe(this._umgebungen, () => this.umgebungService.list(this._projektId()));
    this.ladeListe(this._projektStufen, () => this.stufenService.list(this._projektId()));
  }

  /** Übernimmt einen Entwurf, etwa nach einem gescheiterten Speichern beim Anlegen des Plans. */
  setzeEntwurf(pakete: PaketEntwurf[]): void {
    const nummern = pakete.map((p) => /^neu-(\d+)$/.exec(p.key)?.[1]).filter((n) => n !== undefined);
    this.neueSchluessel = Math.max(this.neueSchluessel, ...nummern.map(Number));
    this._entwurf.set(pakete);
  }

  /**
   * Eine Teststufe, die nicht zum Release des Plans gehört, wird im Entwurf geleert (P29); das Backend nähme sie
   * nicht an (NEX-2288). Schreibt nur bei einer Änderung, damit ein Effekt darauf nicht erneut auslöst.
   */
  entferneFremdeStufen(release: Release): void {
    const imRelease = new Set((release.testStages ?? []).map((s) => s.stage));
    const fremd = (p: PaketEntwurf) => p.projectTestStageId !== null && !imRelease.has(p.projectTestStageId);
    if (this._entwurf().some(fremd)) {
      this._entwurf.update((pakete) => pakete.map((p) => (fremd(p) ? { ...p, projectTestStageId: null } : p)));
    }
  }

  /**
   * Das Backend nimmt beim Speichern des Plans entfernte Testfälle aus den Paketen; gespeicherter Stand und
   * Entwurf folgen dem gleich, damit `geaendert` unverändert bleibt.
   */
  behalteNurTestfaelle(testCaseIds: readonly string[]): void {
    const imPlan = new Set(testCaseIds);
    const kuerzen = <T extends { testCaseIds: string[] }>(p: T): T => ({
      ...p,
      testCaseIds: p.testCaseIds.filter((id) => imPlan.has(id)),
    });
    this._gespeichert.update((pakete) => pakete.map(kuerzen));
    this._entwurf.update((pakete) => pakete.map(kuerzen));
  }

  aendere(key: string, aenderung: PaketAenderung): void {
    this._entwurf.update((pakete) => pakete.map((p) => (p.key === key ? { ...p, ...aenderung } : p)));
  }

  entferne(key: string): void {
    this._entwurf.update((pakete) => pakete.filter((p) => p.key !== key));
    this._markiert.update((keys) => keys.filter((k) => k !== key));
  }

  schalteUmgebung(key: string, umgebung: string): void {
    const paket = this._entwurf().find((p) => p.key === key);
    if (!paket) {
      return;
    }
    const gewaehlt = enthaelt(paket.environments, umgebung);
    this.aendere(key, {
      environments: gewaehlt
        ? paket.environments.filter((u) => !gleicheUmgebung(u, umgebung))
        : [...paket.environments, umgebung],
    });
  }

  /** Wie im Design: Die Umgebung der gewählten Release-Teststufe kommt vorne dazu, wenn sie fehlt. */
  waehleStufe(key: string, stufeId: string | null, release: Release | null): void {
    const paket = this._entwurf().find((p) => p.key === key);
    if (!paket) {
      return;
    }
    const umgebung = release?.testStages?.find((s) => s.stage === stufeId && s.environment)?.environment;
    const neueUmgebung = umgebung && !enthaelt(paket.environments, umgebung) ? [this.schreibweise(umgebung)] : [];
    this.aendere(key, { projectTestStageId: stufeId, environments: [...neueUmgebung, ...paket.environments] });
  }

  /** Legt sofort an, nicht erst beim Speichern: `PUT …/packages` nimmt nur Umgebungen an, die es im Projekt gibt. */
  legeUmgebungAn(key: string, name: string): Observable<ProjectEnvironment> {
    return defer(() => {
      this._umgebungAnlage.set(true);
      return this.umgebungService.create(this._projektId(), {
        name,
        url: null,
        username: null,
        password: null,
        defaultTdbId: null,
      });
    }).pipe(
      tap((umgebung) => {
        this._umgebungen.update((liste) => ({ ...liste, eintraege: [...liste.eintraege, umgebung] }));
        const paket = this._entwurf().find((p) => p.key === key);
        if (paket && !enthaelt(paket.environments, umgebung.name)) {
          this.aendere(key, { environments: [...paket.environments, umgebung.name] });
        }
      }),
      finalize(() => this._umgebungAnlage.set(false)),
    );
  }

  /** Klappt das Paket auf; ist es schon offen, klappt es wieder zu. */
  schalteOffen(key: string): void {
    this._offenKey.update((offen) => (offen === key ? null : key));
  }

  oeffne(key: string): void {
    this._offenKey.set(key);
  }

  oeffneDialog(paketKey: string | null): void {
    this._dialog.set({ paketKey });
  }

  schliesseDialog(): void {
    this._dialog.set(null);
  }

  /** Ein neues Paket übernimmt die erste Teststufe des Release mit ihrer Umgebung; ohne Release-Teststufe keine (AK1). */
  uebernehmeDialog(ergebnis: PaketDialogErgebnis, release: Release | null): void {
    const dialog = this._dialog();
    if (!dialog) {
      return;
    }
    const name = ergebnis.name.trim();
    if (dialog.paketKey === null) {
      this._entwurf.update((pakete) => [...pakete, this.neuesPaket(name, ergebnis.testCaseIds, release)]);
    } else {
      this.aendere(dialog.paketKey, { testCaseIds: [...ergebnis.testCaseIds], ...(name ? { name } : {}) });
    }
    this._dialog.set(null);
  }

  /** Jede Filteränderung zeigt wieder die ersten 25 Pakete (P18). */
  setzeSuche(suche: string): void {
    this.setzeFilter({ suche });
  }

  setzeFilterStufe(stufeId: string): void {
    this.setzeFilter({ stufe: stufeId });
  }

  setzeFilterUmgebung(umgebung: string): void {
    this.setzeFilter({ umgebung });
  }

  schalteNurUnvollstaendig(): void {
    this.setzeFilter({ nurUnvollstaendig: !this._filter().nurUnvollstaendig });
  }

  mehrLaden(): void {
    this._anzahl.update((n) => n + MEHR_LADEN);
  }

  /**
   * Pakete nach Suche (Paketname, Testfall-Schlüssel, ohne Groß-/Kleinschreibung), Teststufe, Umgebung
   * und Vollständigkeit; zählt nur Testfälle, die noch im Plan ausgewählt sind.
   */
  gefiltert(testfaelle: readonly PaketTestfall[]): PaketEntwurf[] {
    const { suche, stufe, umgebung, nurUnvollstaendig } = this._filter();
    const nachId = new Map(testfaelle.map((t) => [t.id, t.key]));
    const text = suche.trim().toLocaleLowerCase();
    return this._entwurf().filter((paket) => {
      const keys = paket.testCaseIds.flatMap((id) => nachId.get(id) ?? []);
      const imPlan = { ...paket, testCaseIds: paket.testCaseIds.filter((id) => nachId.has(id)) };
      return (
        (!text || [paket.name, ...keys].join(' ').toLocaleLowerCase().includes(text)) &&
        (!stufe || paket.projectTestStageId === stufe) &&
        (!umgebung || paket.environments.includes(umgebung)) &&
        (!nurUnvollstaendig || !this.vollstaendig(imPlan))
      );
    });
  }

  schalteMarkierung(key: string): void {
    this._markiert.update((keys) => (keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]));
  }

  /** Wie die Kopf-Checkbox: Sind alle markiert, hebt sie die Markierung dieser Pakete auf, sonst markiert sie sie. */
  schalteAlle(keys: string[]): void {
    const markiert = this._markiert();
    const alle = keys.length > 0 && keys.every((k) => markiert.includes(k));
    this._markiert.set(alle ? markiert.filter((k) => !keys.includes(k)) : [...new Set([...markiert, ...keys])]);
  }

  hebeMarkierungAuf(): void {
    this._markiert.set([]);
  }

  setzeStufeFuerMarkierte(stufeId: string): void {
    this.aendereMarkierte(() => ({ projectTestStageId: stufeId }));
  }

  fuegeUmgebungZuMarkierten(umgebung: string): void {
    this.aendereMarkierte((p) =>
      enthaelt(p.environments, umgebung) ? {} : { environments: [...p.environments, this.schreibweise(umgebung)] },
    );
  }

  /** Ohne eigene Rückfrage wie im Design; wirksam erst mit „Speichern“ (P19). */
  entferneMarkierte(): void {
    const markiert = new Set(this._markiert());
    this._entwurf.update((pakete) => pakete.filter((p) => !markiert.has(p.key)));
    this._markiert.set([]);
  }

  /** Startet den Probelauf des gespeicherten Pakets; bei einem Fehler ist das Paket wieder startbar. */
  probelauf(key: string): Observable<CreatedTestRun[]> {
    const id = this._entwurf().find((p) => p.key === key)?.id;
    const planId = this.planId;
    if (!id || !planId) {
      return EMPTY;
    }
    return defer(() => {
      this.setzeLauf(key, { status: 'laeuft' });
      return this.paketService.startTrial(this._projektId(), planId, id);
    }).pipe(
      tap((laeufe) => this.setzeLauf(key, { status: 'fertig', laeufe })),
      catchError((err: unknown) => {
        this.setzeLaufZurueck(key);
        return throwError(() => err);
      }),
    );
  }

  setzeLaufZurueck(key: string): void {
    this._laeufe.update((laeufe) => Object.fromEntries(Object.entries(laeufe).filter(([k]) => k !== key)));
  }

  /**
   * Speichert den Entwurf mit den Testfällen, die noch im Plan sind. Bei einem Fehler bleibt der Entwurf
   * erhalten; 409 `STALE_VERSION` heißt, ein Paket wurde inzwischen anderswo geändert.
   */
  speichern(planId: string, auswahl: readonly string[]): Observable<TestPlanPackage[]> {
    const imPlan = new Set(auswahl);
    const gesendet = this._entwurf();
    const body = gesendet.map((p) => alsRequest(p, imPlan));
    this._speicherFehler.set(null);
    return this.paketService.replace(this._projektId(), planId, body).pipe(
      tap((pakete) => {
        this.planId = planId;
        this.uebernehmeGespeichert(pakete, schluesselNachSpeichern(gesendet, pakete));
      }),
      catchError((err: unknown) => {
        this._speicherFehler.set(isStaleVersion(err) ? 'veraltet' : 'fehler');
        return throwError(() => err);
      }),
    );
  }

  /** Vollständig = mindestens ein Testfall und eine Umgebung (P13). */
  vollstaendig(paket: PaketEntwurf): boolean {
    return paket.testCaseIds.length > 0 && paket.environments.length > 0;
  }

  /**
   * Nur Teststufen des Release; das Backend lehnt andere ab (NEX-2288, `PACKAGE_UNKNOWN_TEST_STAGE`).
   * Die Projekt-Teststufen liefern nur den Namen, falls das Release keinen mitschickt.
   */
  stufenOptionen(release: Release | null): StufenOption[] {
    const projektStufen = this._projektStufen().eintraege;
    const optionen = new Map<string, StufenOption>();
    for (const stufe of release?.testStages ?? []) {
      const name = stufe.stageName ?? projektStufen.find((s) => s.id === stufe.stage)?.name ?? stufe.stage;
      if (!optionen.has(stufe.stage)) {
        optionen.set(stufe.stage, { id: stufe.stage, name });
      }
    }
    return [...optionen.values()];
  }

  private neuesPaket(name: string, testCaseIds: string[], release: Release | null): PaketEntwurf {
    const releaseStufe = release?.testStages?.[0];
    return {
      key: `neu-${++this.neueSchluessel}`,
      id: null,
      name,
      projectTestStageId: releaseStufe?.stage ?? null,
      environments: releaseStufe?.environment ? [this.schreibweise(releaseStufe.environment)] : [],
      testCaseIds: [...testCaseIds],
    };
  }

  private setzeFilter(aenderung: Partial<PaketFilter>): void {
    this._filter.update((f) => ({ ...f, ...aenderung }));
    this._anzahl.set(ERSTE_ANZAHL);
  }

  private aendereMarkierte(aenderung: (paket: PaketEntwurf) => PaketAenderung): void {
    const markiert = new Set(this._markiert());
    this._entwurf.update((pakete) => pakete.map((p) => (markiert.has(p.key) ? { ...p, ...aenderung(p) } : p)));
  }

  private setzeLauf(key: string, lauf: PaketLauf): void {
    this._laeufe.update((laeufe) => ({ ...laeufe, [key]: lauf }));
  }

  /** Umgebungsnamen gelten ohne Groß-/Kleinschreibung; gespeichert wird die Schreibweise des Projekts. */
  private schreibweise(umgebung: string): string {
    return this._umgebungen().eintraege.find((u) => gleicheUmgebung(u.name, umgebung))?.name ?? umgebung;
  }

  /**
   * Neue Pakete bekommen beim Speichern ihre ID als Schlüssel. Markierungen alter Schlüssel fallen weg;
   * das aufgeklappte Paket bleibt offen, solange es einen Nachfolger gibt (`neueSchluessel`).
   */
  private uebernehmeGespeichert(pakete: TestPlanPackage[], neueSchluessel = new Map<string, string>()): void {
    this._gespeichert.set(pakete);
    this._entwurf.set(pakete.map(alsEntwurf));
    const vorhanden = new Set(pakete.map((p) => p.id));
    this._markiert.update((keys) => keys.filter((k) => vorhanden.has(k)));
    this._offenKey.update((offen) => {
      const nachfolger = offen && (neueSchluessel.get(offen) ?? offen);
      return nachfolger && vorhanden.has(nachfolger) ? nachfolger : null;
    });
  }

  private ladeListe<T>(ziel: WritableSignal<Nachschlag<T>>, quelle: () => Observable<T[]>): void {
    if (ziel().status === 'bereit') {
      return;
    }
    ziel.set({ status: 'laedt', eintraege: [] });
    quelle()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (eintraege) => ziel.set({ status: 'bereit', eintraege }),
        error: () => ziel.set({ status: 'fehler', eintraege: [] }),
      });
  }
}

function vergleichswert(paket: PaketEntwurf | TestPlanPackage): unknown[] {
  return [paket.id, paket.name, paket.projectTestStageId, paket.environments, paket.testCaseIds];
}

/** Das Backend liefert die Pakete in der gesendeten Reihenfolge; als Zuordnung gilt nur ein Treffer mit gleichem Namen. */
function schluesselNachSpeichern(gesendet: PaketEntwurf[], gespeichert: TestPlanPackage[]): Map<string, string> {
  const zuordnung = new Map<string, string>();
  if (gesendet.length === gespeichert.length) {
    gesendet.forEach((alt, i) => {
      if (alt.name.trim() === gespeichert[i].name) {
        zuordnung.set(alt.key, gespeichert[i].id);
      }
    });
  }
  return zuordnung;
}

function alsRequest(paket: PaketEntwurf, imPlan: Set<string>): TestPlanPackageRequest {
  return {
    ...(paket.id ? { id: paket.id } : {}),
    name: paket.name.trim(),
    projectTestStageId: paket.projectTestStageId,
    environments: [...paket.environments],
    testCaseIds: paket.testCaseIds.filter((id) => imPlan.has(id)),
  };
}

export function gleicheUmgebung(a: string, b: string): boolean {
  return a.toLocaleLowerCase() === b.toLocaleLowerCase();
}

export function enthaelt(umgebungen: string[], umgebung: string): boolean {
  return umgebungen.some((u) => gleicheUmgebung(u, umgebung));
}

function alsEntwurf(paket: TestPlanPackage): PaketEntwurf {
  return {
    key: paket.id,
    id: paket.id,
    name: paket.name,
    projectTestStageId: paket.projectTestStageId,
    environments: [...paket.environments],
    testCaseIds: [...paket.testCaseIds],
  };
}
